import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import type { Locale, TranslationKeys } from "../i18n/types";
import en from "../i18n/en.json";
import es from "../i18n/es.json";

export interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: keyof TranslationKeys) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

const STORAGE_KEY = "capcut-srt-locale";
const DEFAULT_LOCALE: Locale = "es";
const SUPPORTED_LOCALES: readonly string[] = ["en", "es"];

const translations: Record<Locale, Record<string, string>> = { en, es };

function isValidLocale(value: unknown): value is Locale {
  return typeof value === "string" && SUPPORTED_LOCALES.includes(value);
}

function getInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isValidLocale(stored)) {
      return stored;
    }
    // Invalid value in localStorage — overwrite with default
    if (stored !== null) {
      localStorage.setItem(STORAGE_KEY, DEFAULT_LOCALE);
    }
  } catch {
    // localStorage unavailable (e.g. private browsing)
  }
  return DEFAULT_LOCALE;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  // Set the initial html lang attribute
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    document.documentElement.lang = newLocale;
    try {
      localStorage.setItem(STORAGE_KEY, newLocale);
    } catch {
      // localStorage unavailable — locale change is session-only
    }
  }, []);

  const t = useCallback(
    (key: keyof TranslationKeys): string => {
      // Look up in active locale
      const value = translations[locale][key];
      if (value !== undefined && value !== "") {
        return value;
      }
      // Fallback to default locale ("es")
      const fallback = translations[DEFAULT_LOCALE][key];
      if (fallback !== undefined && fallback !== "") {
        return fallback;
      }
      // Last resort: return the key itself
      return key;
    },
    [locale]
  );

  const contextValue = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t]
  );

  return (
    <LocaleContext.Provider value={contextValue}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (context === null) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
}
